import { useEffect, useState } from "react";
import { createGfCampaign } from "../../../api/giftingClient";
import { CAMPAIGN_TYPES, CAMPAIGN_TYPE_LABEL, typeHasAffiliate, typeHasFee } from "../../../lib/giftingRules";
import type { GfCampaignType, GfChannels, GfProductMode, GfTabProps } from "../../../types/gifting";
import { errMsg } from "../gfUtil";
import { GfButton, GfChip, GfField, GfModal, GfSeg } from "../ui/GfUi";
import { ReuseDmSetting } from "./ReuseDmSetting";

export interface CampaignFormModalProps extends GfTabProps {
  open: boolean;
  onClose: () => void;
  /** 作成された campaign id */
  onSaved: (campaignId: string) => void;
}

const DEFAULT_CHANNELS: GfChannels = { shopify: true, rakuten: false, amazon: false };

/** 新規案件。タイプに応じて固定報酬・基本料率・計測チャネルを出し分ける */
export function CampaignFormModal({ open, onClose, onSaved, refresh, toast }: CampaignFormModalProps) {
  const [name, setName] = useState("");
  const [type, setType] = useState<GfCampaignType>("gift_affiliate");
  const [fee, setFee] = useState("");
  const [rate, setRate] = useState("10");
  const [reuse, setReuse] = useState<"1" | "0">("1");
  const [reuseDmAuto, setReuseDmAuto] = useState(false);
  const [reuseDmTpl, setReuseDmTpl] = useState<string | null>(null);
  const [channels, setChannels] = useState<GfChannels>(DEFAULT_CHANNELS);
  const [productMode, setProductMode] = useState<GfProductMode>("choice");
  const [productName, setProductName] = useState("");
  const [sku, setSku] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName("");
    setType("gift_affiliate");
    setFee("");
    setRate("10");
    setReuse("1");
    setReuseDmAuto(false);
    setReuseDmTpl(null);
    setChannels(DEFAULT_CHANNELS);
    setProductMode("choice");
    setProductName("");
    setSku("");
    setQuantity("1");
    setError(null);
  }, [open]);

  const hasFee = typeHasFee(type);
  const hasAff = typeHasAffiliate(type);

  const submit = async () => {
    const feeNum = fee.trim() === "" ? null : Number(fee);
    const rateNum = Number(rate);
    const qty = Number(quantity);
    if (!name.trim()) return setError("案件名を入力してください。");
    if (hasFee && (feeNum === null || !Number.isFinite(feeNum) || feeNum < 0)) return setError("固定報酬を入力してください。");
    if (hasAff && (!Number.isFinite(rateNum) || rateNum < 0 || rateNum > 30)) return setError("基本料率は 0〜30% で入力してください。");
    if (!Number.isInteger(qty) || qty < 1) return setError("数量は1以上の整数で入力してください。");
    if (productMode === "fixed" && !productName.trim()) return setError("固定の商品のときは商品名を入力してください。");
    setBusy(true);
    setError(null);
    try {
      const c = await createGfCampaign({
        name: name.trim(),
        type,
        fee_amount: hasFee ? feeNum : null,
        base_rate: hasAff ? Math.round(rateNum * 10) / 1000 : null,
        reuse_required: reuse === "1" ? 1 : 0,
        ...(reuse === "1" && reuseDmAuto ? { reuse_dm_auto: 1 as const, reuse_dm_template_id: reuseDmTpl } : {}),
        channels: hasAff ? channels : DEFAULT_CHANNELS,
        product_mode: productMode,
        ...(productMode === "fixed" ? { sku: sku.trim() || null, product_name: productName.trim() || null } : {}),
        quantity: qty,
      });
      await refresh();
      toast(`案件「${c.name}」を作成しました`);
      onSaved(c.id);
      onClose();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <GfModal
      open={open}
      title="新規案件"
      onClose={onClose}
      footer={
        <>
          <GfButton onClick={onClose} disabled={busy}>
            やめる
          </GfButton>
          <GfButton variant="primary" disabled={busy} onClick={() => void submit()}>
            {busy ? "作成中…" : "案件を作成"}
          </GfButton>
        </>
      }
    >
      <GfField label="案件名（必須）">
        <input className="gf-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="例: 冬のギフトBOX 2026" autoFocus />
      </GfField>
      <GfField label="案件タイプ">
        <GfSeg
          ariaLabel="案件タイプ"
          value={type}
          onChange={setType}
          options={CAMPAIGN_TYPES.map((t) => ({ value: t, label: CAMPAIGN_TYPE_LABEL[t] }))}
        />
      </GfField>
      {hasFee && (
        <GfField label="固定報酬（円・1名あたり）">
          <input className="gf-input is-mono" inputMode="numeric" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="50000" />
        </GfField>
      )}
      {hasAff && (
        <>
          <GfField label="基本料率（%・0〜30）" hint="個別料率が設定されたクリエイターは、そちらが優先されます">
            <input className="gf-input is-mono" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} />
          </GfField>
          <GfField label="計測チャネル">
            <div className="gf-chips">
              <GfChip active={channels.shopify} onClick={() => setChannels({ ...channels, shopify: !channels.shopify })}>
                Shopify
              </GfChip>
              <GfChip active={channels.rakuten} onClick={() => setChannels({ ...channels, rakuten: !channels.rakuten })}>
                楽天
              </GfChip>
              <GfChip active={channels.amazon} onClick={() => setChannels({ ...channels, amazon: !channels.amazon })}>
                Amazon
              </GfChip>
            </div>
          </GfField>
        </>
      )}
      <GfField label="2次利用" hint="「なし」の案件は、投稿確認のあとそのままアーカイブされます">
        <GfSeg
          ariaLabel="2次利用"
          value={reuse}
          onChange={setReuse}
          options={[
            { value: "1", label: "あり" },
            { value: "0", label: "なし" },
          ]}
        />
      </GfField>
      {reuse === "1" && <ReuseDmSetting auto={reuseDmAuto} templateId={reuseDmTpl} onAuto={setReuseDmAuto} onTemplate={setReuseDmTpl} />}
      <GfField
        label="送る商品"
        hint={
          productMode === "choice"
            ? "本人フォームで、セット6種から1つ選んでもらいます（選べるセットは「報酬・料率」で絞り込めます）"
            : "全員に同じ商品を送ります"
        }
      >
        <GfSeg<GfProductMode>
          ariaLabel="送る商品の決め方"
          value={productMode}
          onChange={setProductMode}
          options={[
            { value: "choice", label: "クリエイターが選ぶ" },
            { value: "fixed", label: "固定の商品" },
          ]}
        />
      </GfField>
      <div className="gf-form-grid">
        {productMode === "fixed" && (
          <>
            <GfField label="商品名（必須）">
              <input className="gf-input" value={productName} onChange={(e) => setProductName(e.target.value)} />
            </GfField>
            <GfField label="SKU">
              <input className="gf-input is-mono" value={sku} onChange={(e) => setSku(e.target.value)} />
            </GfField>
          </>
        )}
        <GfField label="数量">
          <input className="gf-input is-mono" inputMode="numeric" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        </GfField>
      </div>
      {error && <div className="gf-banner is-error">{error}</div>}
    </GfModal>
  );
}
